import React, { useState } from 'react';
import { ExternalLink, Wallet, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { handleActionError, actionSuccess } from '../../utils/actionFee';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EndorsementsList = ({ items, light }) => (
  <div className={light ? 'mt-4' : 'bg-slate-800 border border-slate-700 rounded-xl p-5'} data-testid="tc-endorsements">
    <h3 className={`font-semibold mb-2 ${light ? 'text-gray-900 text-sm' : 'text-white'}`}>Endorsements ({items.length})</h3>
    {items.length ? items.map(e => (
      <div key={e.endorsement_no} className={`text-xs py-1.5 border-b ${light ? 'border-gray-200 text-gray-700' : 'border-slate-700 text-slate-300'}`} data-testid={`tc-endorsement-${e.endorsement_no}`}>
        <p><b>#{e.endorsement_no}</b> {new Date(e.endorsed_at).toLocaleString()} · Pay to the order of <b>{e.assignee?.name || e.to_address}</b> · by {e.endorser?.name}
          {e.wallet_signed && <span className="ml-2 text-emerald-400 text-[10px]">✓ Wallet Signed</span>}
        </p>
        <p className="font-mono text-[10px] break-all opacity-80">{e.from_address} → {e.to_address} · hash {e.endorsement_hash}</p>
        <p className="flex gap-3">
          <a href={e.transfer_explorer_url} target="_blank" rel="noreferrer" className="text-emerald-600 inline-flex items-center gap-1">Transfer tx <ExternalLink className="w-3 h-3" /></a>
          {e.anchor_explorer_url && <a href={e.anchor_explorer_url} target="_blank" rel="noreferrer" className="text-emerald-600 inline-flex items-center gap-1">Endorsement record <ExternalLink className="w-3 h-3" /></a>}
          {e.memo && <span>Memo: {e.memo}</span>}
        </p>
      </div>
    )) : <p className={`text-xs ${light ? 'text-gray-500' : 'text-slate-500'}`}>No endorsements yet</p>}
  </div>
);

export const EndorseForm = ({ busy, onEndorse, check, token }) => {
  const [to, setTo] = useState('');
  const [memo, setMemo] = useState('');
  const [walletSigning, setWalletSigning] = useState(false);
  
  const hasWallet = typeof window.ethereum !== 'undefined';

  const standardEndorse = () => {
    onEndorse({ to: to.trim(), memo: memo.trim() });
  };

  const walletSignEndorse = async () => {
    if (!check?.check_id || !token) return;
    
    setWalletSigning(true);
    try {
      // Step 1: Prepare the endorsement message
      const prepRes = await axios.post(
        `${API}/pas-treasury-check/checks/${check.check_id}/endorse/prepare`,
        { to: to.trim(), memo: memo.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Step 2: Request signature from wallet
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      if (!accounts.length) {
        toast.error('Please connect your wallet first');
        return;
      }

      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [prepRes.data.message, accounts[0]]
      });

      // Step 3: Submit the signed endorsement
      const submitRes = await axios.post(
        `${API}/pas-treasury-check/checks/${check.check_id}/endorse/submit`,
        { 
          to: to.trim(), 
          memo: memo.trim(),
          signature: signature,
          message_hash: prepRes.data.message_hash
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      actionSuccess(submitRes.data);
      setTo('');
      setMemo('');
      // Trigger reload
      if (typeof onEndorse === 'function') {
        onEndorse({ _reload: true });
      }

    } catch (err) {
      console.error('Wallet endorsement error:', err);
      if (err.code === 4001) {
        toast.error('Signature request was rejected');
      } else if (err.response?.data?.detail) {
        handleActionError(err, token, 'Failed to endorse with wallet');
      } else {
        toast.error(err.message || 'Failed to endorse with wallet');
      }
    } finally {
      setWalletSigning(false);
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5" data-testid="tc-endorse-form">
      <h3 className="text-white font-semibold">Endorse this check</h3>
      <p className="text-xs text-slate-400 mb-3">Transfers the full check to a new assignee and records the endorsement on-chain.</p>
      <div className="flex flex-wrap gap-2">
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Assignee wallet or subscriber email" data-testid="tc-endorse-to" className="flex-1 min-w-[14rem] bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
        <input value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Memo (optional)" data-testid="tc-endorse-memo" className="flex-1 min-w-[10rem] bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={standardEndorse} disabled={busy || to.trim().length < 3} data-testid="tc-endorse-btn"
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm disabled:opacity-40">
          {busy ? 'Endorsing...' : 'Endorse'}
        </button>
        {hasWallet && check?.check_id && token && (
          <button onClick={walletSignEndorse} disabled={busy || walletSigning || to.trim().length < 3} data-testid="tc-endorse-wallet-btn"
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm disabled:opacity-40 flex items-center gap-2">
            {walletSigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
            {walletSigning ? 'Sign in wallet...' : 'Sign & Endorse with Wallet'}
          </button>
        )}
      </div>
      {!hasWallet && (
        <p className="text-xs text-amber-400 mt-2">Install MetaMask to sign endorsements with your wallet</p>
      )}
    </div>
  );
};
